// Tab „Notizen“ (Handoff Sonstige Kontakte §2, Screen 08). Logik wie bisher in KontaktNotizen:
// Eingabe „Neue Notiz“ + „Notiz hinzufügen“, Liste zweispaltig (ab 1100 px), Löschen je Notiz.
import { useEffect, useState } from 'react';
import { CircleNotch, Note, Plus, Trash } from '@phosphor-icons/react';
import { MeldungDialog } from '../../../../components/ui/MeldungDialog';
import { ladeNotizen, loescheNotiz, speichereNotiz } from '../../../../lib/kontakte/api';
import { meldeKontaktGeaendert } from '../../../../lib/kontakte/detailTab';
import type { KontaktNotiz } from '../../../../lib/kontakte/types';
import { LeerKarte, TabLaedt } from './teile';

const fehlerText = (err: unknown) => (err instanceof Error ? err.message : 'Unbekannter Fehler');

export function SonstigeNotizen({ id }: { id: string }) {
  const [notizen, setNotizen] = useState<KontaktNotiz[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = () => {
    setLaedt(true);
    ladeNotizen('kontakt', id).then((data) => { setNotizen(data); setLaedt(false); });
  };
  useEffect(() => { laden(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const handleSpeichern = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await speichereNotiz('kontakt', id, trimmed);
      setText('');
      laden();
      meldeKontaktGeaendert();
    } catch (err) {
      setMeldung('Notiz nicht gespeichert: ' + fehlerText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleLoeschen = async (notizId: string) => {
    try {
      await loescheNotiz(notizId);
      laden();
      meldeKontaktGeaendert();
    } catch (err) {
      setMeldung('Notiz nicht gelöscht: ' + fehlerText(err));
    }
  };

  const gesperrt = saving || !text.trim();

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <section aria-label="Neue Notiz" className="sk-glas sk-rise relative flex flex-col" style={{ isolation: 'isolate', overflow: 'hidden', borderRadius: 26, padding: 16, gap: 10 }}>
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{ zIndex: -1, background: 'radial-gradient(80% 80% at 100% 0%, var(--warn-soft), transparent 60%)' }} />
        <h2 className="m-0 flex items-center" style={{ gap: 10, fontSize: 14, fontWeight: 800 }}>
          <span aria-hidden className="flex items-center justify-center"
            style={{ width: 30, height: 30, borderRadius: 10, color: 'var(--sk-av-ink)', background: 'linear-gradient(155deg, var(--sk-notiz-a), var(--sk-notiz-b))' }}>
            <Note weight="fill" size={15} />
          </span>
          Neue Notiz
        </h2>
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Notiz eingeben …" rows={6} aria-label="Neue Notiz"
          className="w-full"
          style={{ minHeight: 140, resize: 'vertical', padding: 14, border: 'none', borderRadius: 16, background: 'var(--sk-field)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink)', outline: 'none' }} />
        <button type="button" onClick={() => void handleSpeichern()} disabled={gesperrt}
          data-tip="Notiz hinzufügen – Wird auch in der Aktivität angezeigt"
          className="sk-akzent flex cursor-pointer items-center justify-center disabled:cursor-not-allowed"
          style={{ height: 42, border: 'none', borderRadius: 21, gap: 7, fontSize: 13, fontWeight: 800, color: 'var(--acc-ink)', background: 'var(--acc-fill)', opacity: gesperrt ? 0.4 : 1 }}>
          {saving ? <CircleNotch size={14} className="animate-spin" /> : <Plus weight="bold" size={14} />}
          Notiz hinzufügen
        </button>
      </section>

      {laedt ? (
        <TabLaedt />
      ) : notizen.length === 0 ? (
        <LeerKarte icon={Note} text="Keine Notizen" />
      ) : (
        <div className="sk-spalten">
          {notizen.map((n) => (
            <div key={n.id} className="sk-glas sk-rise sk-notiz relative" style={{ breakInside: 'avoid', marginBottom: 10, borderRadius: 20, paddingTop: 14, paddingBottom: 12, paddingLeft: 14, animationDuration: '.4s' }}>
              <p className="m-0 whitespace-pre-wrap" style={{ fontSize: 13.5, lineHeight: 1.55, overflowWrap: 'anywhere' }}>{n.text}</p>
              <span className="immo-zahl block" style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>
                {new Date(n.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
              <button type="button" onClick={() => void handleLoeschen(n.id)} data-tip="Notiz löschen" aria-label="Notiz löschen"
                className="sk-loesen sk-tipp absolute flex cursor-pointer items-center justify-center"
                style={{ top: 10, right: 10, width: 28, height: 28, border: 'none', borderRadius: 9 }}>
                <Trash weight="bold" size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      {meldung && <MeldungDialog fehler text={meldung} onSchliessen={() => setMeldung(null)} />}
    </div>
  );
}
